import React, { useState, useEffect } from "react"
import { getAdmin, getAPIBaseURL } from "../../services/helpers"
import { updateData } from "../../services/request"

const Profile = () => {
  const [admin, setAdmin] = useState(getAdmin())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [genMsg, setGenMsg] = useState("")

  // Profile edit state
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState("")
  const [username, setUsername] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")

  // Helper function to format phone number with leading zero
  const formatPhoneWithLeadingZero = (phone) => {
    if (!phone) return ""
    const phoneStr = phone.toString()
    // If it doesn't start with 0, add it. If it's 10 digits (missing leading 0), add 0
    if (phoneStr.length === 10) {
      return `0${phoneStr}`
    }
    if (phoneStr.length === 11 && !phoneStr.startsWith('0')) {
      return `0${phoneStr}`
    }
    // If already 11 digits with leading 0, return as is
    if (phoneStr.length === 11 && phoneStr.startsWith('0')) {
      return phoneStr
    }
    // If less than 10 digits, ensure it starts with 0
    if (phoneStr.length < 10) {
      return phoneStr.startsWith('0') ? phoneStr : `0${phoneStr}`
    }
    return phoneStr
  }

  useEffect(() => {
    const currentAdmin = getAdmin()
    if (currentAdmin) {
      setAdmin(currentAdmin)
      if (!editing) {
        // Only set form values if not editing to avoid overwriting user input
        setName(currentAdmin.name || "")
        setUsername(currentAdmin.username || "")
        const formattedPhone = formatPhoneWithLeadingZero(currentAdmin.phoneNumber)
        setPhoneNumber(formattedPhone)
      }
    }
  }, [editing])

  const handleEdit = () => {
    setEditing(true)
    setError("")
    setGenMsg("")
  }

  const handleCancelEdit = () => {
    setEditing(false)
    if (admin) {
      setName(admin.name || "")
      setUsername(admin.username || "")
      const formattedPhone = formatPhoneWithLeadingZero(admin.phoneNumber)
      setPhoneNumber(formattedPhone)
    }
  }

  const handleUpdateProfile = async () => {
    setError("")
    setGenMsg("")

    if (!name || name.trim().length < 2) {
      setError("Name must be at least 2 characters")
      return
    }
    if (!username || username.trim().length < 2) {
      setError("Username must be at least 2 characters")
      return
    }
    // Ensure phone number starts with 0 and is 11 digits
    const cleanedPhone = phoneNumber.trim().replace(/\D/g, '')
    if (!cleanedPhone || cleanedPhone.length !== 11) {
      setError("Phone number must be 11 digits")
      return
    }
    if (!cleanedPhone.startsWith('0')) {
      setError("Phone number must start with 0")
      return
    }

    setLoading(true)
    const url = `${getAPIBaseURL()}/v1/admins/update-self`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('name', name.trim())
    params.append('username', username.trim())
    
    // Use cleanedPhone that was already validated above
    const phoneToSend = cleanedPhone.startsWith('0') ? cleanedPhone : `0${cleanedPhone}`
    params.append('phoneNumber', phoneToSend)
    
    const res = await updateData(url, headers, params.toString())
    setLoading(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setGenMsg("Profile updated successfully")
        // Update localStorage - store as string to preserve leading zero
        const updatedAdmin = { ...admin, name, username, phoneNumber: phoneToSend }
        localStorage.setItem('admin', JSON.stringify(updatedAdmin))
        setAdmin(updatedAdmin)
        setPhoneNumber(phoneToSend)
        setEditing(false)
      } else {
        setError(res.result.data?.message || 'Could not update profile')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso || '—'
    }
  }

  const formatRole = (role) => {
    return role?.replace(/_/g, ' ') || '—'
  }

  if (!admin) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
          No profile data available. Please log in again.
        </div>
      </div>
    )
  }

  return (
    <section className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="mt-1 text-sm text-gray-600">Manage your account information and security settings</p>
      </div>

      {/* Profile Information */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
            {!editing && (
              <button
                onClick={handleEdit}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-6">
          {error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
          ) : null}
          {genMsg ? (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">{genMsg}</div>
          ) : null}

          {editing ? (
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateProfile(); }} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="profile-name" className="mb-1 block text-xs font-medium text-gray-900">Name *</label>
                  <input
                    id="profile-name"
                    type="text"
                    name="name"
                    value={name || ""}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    required
                    disabled={loading}
                    className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label htmlFor="profile-username" className="mb-1 block text-xs font-medium text-gray-900">Username *</label>
                  <input
                    id="profile-username"
                    type="text"
                    name="username"
                    value={username || ""}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                    disabled={loading}
                    className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label htmlFor="profile-phone" className="mb-1 block text-xs font-medium text-gray-900">Phone Number *</label>
                  <input
                    id="profile-phone"
                    type="tel"
                    name="phoneNumber"
                    value={phoneNumber || ""}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '')
                      // Ensure it always starts with 0
                      if (value.length > 0 && !value.startsWith('0')) {
                        value = `0${value}`
                      }
                      // Limit to 11 digits
                      if (value.length > 11) {
                        value = value.substring(0, 11)
                      }
                      setPhoneNumber(value)
                    }}
                    maxLength={11}
                    autoComplete="tel"
                    required
                    disabled={loading}
                    placeholder="08012345678"
                    className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">11 digits required, must start with 0</p>
                </div>
                <div>
                  <label htmlFor="profile-role" className="mb-1 block text-xs font-medium text-gray-900">Role</label>
                  <input
                    id="profile-role"
                    type="text"
                    value={formatRole(admin.role)}
                    disabled
                    readOnly
                    className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={loading}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <div className="text-xs font-medium text-gray-500">Name</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{admin.name || '—'}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500">Username</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{admin.username || '—'}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500">Phone Number</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{admin.phoneNumber ? formatPhoneWithLeadingZero(admin.phoneNumber) : '—'}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500">Role</div>
                <div className="mt-1">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                    admin.role === 'SUPER_ADMIN' || admin.role === 'SUPER_MODERATOR' 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {formatRole(admin.role)}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500">Status</div>
                <div className="mt-1">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                    admin.status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {admin.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500">Created At</div>
                <div className="mt-1 text-sm text-gray-900">{formatDate(admin.createdAt)}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
        Password updates are handled by a Super Admin. Please reach out to request a password reset.
      </div>
    </section>
  )
}

export default Profile
